/**
 * Subscription Entity Schemas
 *
 * Mirrors the Prisma `Subscription` + `SubscriptionStatus` /
 * `CustomerOwnerType` enums (apps/backend/prisma/schema/base.prisma).
 * `Subscription` is the SINGLE source of truth for who-has-what (KTD-2),
 * polymorphic-owned (SCHOOL | USER, KTD-1), one per (owner, product).
 *
 * Restricted-read (R5): the billing linkage fields `billingProvider` /
 * `billingExternalRef` are surfaced ONLY on the super-admin variant
 * (`SubscriptionAdminSchema`). The base/school-admin/member variant
 * (`SubscriptionSchema`) deliberately omits them so they never reach a
 * non-super-admin client. The server strips them before serialization; this
 * split is the type-level expression of that boundary.
 *
 * `publicId` is exposed as `id`; raw BigInt internal ids are never surfaced.
 */

import { z } from 'zod'
import { ProductSummarySchema } from './product'
import { PlanSummarySchema } from './plan'
import { SubscriptionAddOnSchema } from './add-on'

/**
 * Subscription lifecycle status. Mirrors the `SubscriptionStatus` Prisma
 * enum. Only `trial` and `paying` are entitling (KTD-5).
 */
export const SubscriptionStatusSchema = z.enum(['trial', 'paying', 'churned', 'expired', 'cancelled'])
export type SubscriptionStatus = z.infer<typeof SubscriptionStatusSchema>

/**
 * Statuses that contribute grants in the resolver (entitling). Exported so
 * resolver + admin panel share one definition (KTD-5/KTD-11 single selector).
 */
export const ENTITLING_SUBSCRIPTION_STATUSES = ['trial', 'paying'] as const

/**
 * Polymorphic customer owner type. Mirrors the `CustomerOwnerType` Prisma
 * enum. Only SCHOOL is written this phase; USER is modeled for B2C.
 */
export const CustomerOwnerTypeSchema = z.enum(['SCHOOL', 'USER'])
export type CustomerOwnerType = z.infer<typeof CustomerOwnerTypeSchema>

/**
 * The action that produced a `SubscriptionTransition` row (KTD-12). Mirrors the
 * `action` discriminator written by the subscription lifecycle service.
 */
export const SubscriptionTransitionActionSchema = z.enum([
  'assign',
  'flip-status',
  'plan-change',
  'attach-add-on',
  'detach-add-on',
  'remove',
  'seed'
])
export type SubscriptionTransitionAction = z.infer<typeof SubscriptionTransitionActionSchema>

/**
 * A single append-only plan/status transition (KTD-12). Records from/to plan +
 * status, the action, and the acting principal's public id. Billing linkage is
 * NEVER present here. Surfaced only on the super-admin subscription variant.
 */
export const SubscriptionTransitionSchema = z.object({
  id: z.string().describe('Transition public ID (cuid)'),
  action: SubscriptionTransitionActionSchema.describe('What produced the transition'),
  fromPlanId: z.string().nullable().describe('Plan public ID before the transition (null on first assign/seed)'),
  toPlanId: z.string().nullable().describe('Plan public ID after the transition'),
  fromStatus: SubscriptionStatusSchema.nullable().describe('Status before the transition'),
  toStatus: SubscriptionStatusSchema.describe('Status after the transition'),
  actor: z.string().nullable().describe('Acting principal User public ID (null for system/seed)'),
  reason: z.string().nullable().describe('Optional free-text reason'),
  createdAt: z.string().describe('ISO timestamp')
})
export type SubscriptionTransition = z.infer<typeof SubscriptionTransitionSchema>

/**
 * A single per-subscription entitlement delta. Each delta records its grantor
 * subscription for provenance (KTD-3) so the enforcement phase can drop
 * deltas when a subscription stops entitling, with no later migration.
 */
export const SubscriptionDeltaSchema = z.object({
  componentSlug: z.string().describe('The Component.slug this delta overrides'),
  enabled: z.boolean().describe('Whether the feature is granted by this delta'),
  config: z.record(z.string(), z.unknown()).optional().describe('Optional config override for the component'),
  grantor: z.string().describe('Public ID of the subscription that granted this delta (provenance, KTD-3)'),
  grantedAt: z.string().optional().describe('ISO timestamp the delta was granted'),
  reason: z.string().optional().describe('Optional human note for the delta')
})

export type SubscriptionDelta = z.infer<typeof SubscriptionDeltaSchema>

/**
 * Base subscription shape WITHOUT the restricted billing linkage (R5).
 * This is the school-admin / member variant. The billing ref is intentionally
 * absent — see `SubscriptionAdminSchema` for the super-admin variant.
 */
export const SubscriptionSchema = z.object({
  id: z.string().describe('Subscription public ID (cuid)'),
  ownerType: CustomerOwnerTypeSchema.describe('Polymorphic owner type (SCHOOL | USER)'),
  ownerId: z.string().describe('Owner public ID (School or User), never the raw internal id'),
  product: ProductSummarySchema.describe('The product this subscription is for'),
  plan: PlanSummarySchema.describe('The baseline plan grant set'),
  status: SubscriptionStatusSchema.describe('Lifecycle status'),
  addOns: z.array(SubscriptionAddOnSchema).describe('Attached add-ons'),
  overrideDeltas: z.array(SubscriptionDeltaSchema).nullable().describe('Explicit per-subscription grant deltas'),
  trialEndsAt: z.string().nullable().describe('ISO timestamp the trial ends, if applicable'),
  createdAt: z.string().describe('ISO timestamp'),
  updatedAt: z.string().nullable().describe('ISO timestamp')
})

export type Subscription = z.infer<typeof SubscriptionSchema>

/**
 * Super-admin variant: the base subscription PLUS the restricted billing
 * linkage (R5). Only emitted when the principal passes `requireSuperAdmin`.
 */
export const SubscriptionAdminSchema = SubscriptionSchema.extend({
  billingProvider: z.string().nullable().describe('Billing provider, e.g. hubspot/stripe/clerk (super-admin only)'),
  billingExternalRef: z
    .string()
    .nullable()
    .describe('External billing reference / provider subscription id (restricted-read, super-admin only)'),
  /**
   * Append-only plan/status transition history (KTD-12), most recent first,
   * capped. Surfaced on the super-admin variant so the manage-school UI can show
   * history without a separate endpoint. Billing linkage is NEVER recorded in a
   * transition.
   */
  transitions: z
    .array(SubscriptionTransitionSchema)
    .describe('Recent plan/status transitions, newest first (super-admin only)')
})

export type SubscriptionAdmin = z.infer<typeof SubscriptionAdminSchema>
